import React, { useEffect, useState } from 'react';
import { injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import mediaQueries from 'app/utils/mediaQueries';
import LoadingIndicator from 'app/products/components/productList/loadingIndicator';
import { getElementPosition } from 'app/utils/helpers';
import { debounce, isEmpty } from 'lodash';
import ProductList from './components/productList/productListContainer';
import FacetsList from './components/facetsList/facetsListContainer';
import SortDropdown from './components/sortDropdown/sortDropdownContainer';
import SearchInputPortal from './components/searchInput/searchInputPortal';
import './products.scss';
import FacetsListLargeUp from './components/facetsList/facetsListLargeUp';
import { getFacetIds } from './components/facetsList/availableFacetsReducer';
import { getAllAppliedFacetItemUids } from './productsReducer';
import ListTitle from './components/listTitle/listTitle';

const NoResults = React.lazy(() =>
  import(
    /* webpackChunkName: "noResultsContainer" */
    './components/noResults/noResultsContainer'
  )
);

const ProductsApp = props => {
  const {
    totalItemsResult,
    noResultsTemplate,
    facetIds,
    appliedItemsCount,
    numberOfProducts,
    isFetching,
    showLoader,
    error,
  } = props;
  const noResultsTemplateAvailable = noResultsTemplate !== '';
  const [isMediumDown, setIsMediumDown] = useState(!mediaQueries.is_large_up());
  const [stickyStyle, setStickyStyle] = useState({});
  const [isNoResultsTemplateLoaded, setIsNoResultsTemplateLoaded] = useState(false);

  const onScroll = () => {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const plpPosition = getElementPosition(document.getElementById('plp'));
    const cmsStickyBannerHeight = document.querySelector('.sticky-banner-wrapper')?.offsetHeight ?? 0;
    const headerSize = document.querySelector('header').offsetHeight;

    if (scrollTop >= plpPosition) {
      setStickyStyle({
        position: 'fixed',
        top: `${headerSize + cmsStickyBannerHeight}px`,
        zIndex: 10,
      });
    } else {
      setStickyStyle({});
    }
  };

  useEffect(() => {
    if (isMediumDown) {
      window.addEventListener('scroll', onScroll);
    } else {
      setStickyStyle({});
      window.removeEventListener('scroll', onScroll);
    }
    return () => {
      window.removeEventListener('scroll', onScroll);
    };
  }, [isMediumDown]);

  useEffect(() => {
    const onResize = debounce(() => {
      setIsMediumDown(!mediaQueries.is_large_up());
    }, 1000);
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, []);

  return (
    <div className="product-list">
      {noResultsTemplateAvailable && (
        <React.Suspense fallback={<LoadingIndicator fallbackUi />}>
          <NoResults setIsNoResultsTemplateLoaded={setIsNoResultsTemplateLoaded} />
        </React.Suspense>
      )}
      {isNoResultsTemplateLoaded && <SearchInputPortal />}
      {Boolean(totalItemsResult) && (
        <div className="row product-list__product-filter">
          <div style={stickyStyle} className={`${!isEmpty(stickyStyle) ? 'is-sticky' : ''} buttons-wrapper`}>
            <FacetsList />
            <SortDropdown idSuffix="sm" additionalClass="hide-for-large-up" />
          </div>
          <div className="hide-for-large-up">
            <ListTitle isHeader={!mediaQueries.is_large_up()} />
          </div>
        </div>
      )}
      <div className="row products-container">
        {!error && (
          <div className={`large-3 filters-sidebar ${showLoader ? 'is-fetching' : ''}`}>
            <FacetsListLargeUp
              facetIds={facetIds}
              appliedItemsCount={appliedItemsCount}
              numberOfProducts={numberOfProducts}
              isFetching={isFetching}
            />
          </div>
        )}
        <div className={`columns small-12 medium-12 ${error ? 'large-12' : 'large-9'}`}>
          <ProductList appliedItemsCount={appliedItemsCount} />
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = state => ({
  totalItemsResult: state.products && state.products.totalItems > 0,
  noResultsTemplate: state.noResultsTemplate,
  facetIds: getFacetIds(state.filters.available),
  appliedItemsCount: getAllAppliedFacetItemUids(state).length,
  numberOfProducts: state.products.totalItems,
  isFetching: state.isFetching,
  showLoader: state.showLoader,
  error: state.products.error,
});

export default connect(mapStateToProps)(injectIntl(ProductsApp));
